import React from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { selectUser } from 'selectors';
import styled from 'styled-components';
import { Button, Container, Image, responsive, Text } from 'styled-minimal';

import { useAppSelector } from 'modules/hooks';
import { spacer } from 'modules/theme';

import { coaches, grades } from 'config';
import { STATUS } from 'literals';

import { login, setCoach, setGrade } from 'actions';

import Background from 'components/Background';

const StyledContainer = styled(Container)`
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-size: cover;
  background-position: center;
  background-image: url('${process.env.PUBLIC_URL}/media/images/background.png');
`;

const ChooserContainer = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
  flex-direction: column;
  ${responsive({
    xl: {
      flexDirection: 'row',
    },
  })};
`;

const GradeContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 2px solid #000;
  max-width: initial;
  width: 100%;
  ${responsive({
    lg: {
      maxWidth: '720px',
      width: '720px',
    },
    xl: {
      maxWidth: '560px',
      width: '560px',
    },
  })};
`;

const GradeCard = styled.div`
  // margin-bottom: ${spacer(3)};
  // text-align: center;
  border-top: 2px solid black;
  font-size: 2.7rem;
  cursor: pointer;
  padding: 10px;
  display: flex;
  align-items: center;
  background-color: #eee;
  flex-direction: column;
  height: initial;
  text-align: center;
  ${responsive({
    md: {
      flexDirection: 'row',
      height: '160px',
      textAlign: 'left',
    },
  })};

  &:hover {
    background-color: #aaa;
    // background-color: #3057d511;
  }
  &.active {
    background-color: #ffc464;
  }
`;

const Grade = styled.span`
  font-size: 4rem;
  font-weight: 700;
  min-width: 100px;
  text-align: center;
`;

const StepSelect = styled.span`
  font-size: 4rem;
  text-transform: uppercase;
  text-align: center;
  padding: 10px;
`;

const Step = styled.span`
  font-size: 3rem;
  font-weight: 700;
`;

const CoachContainer = styled(GradeContainer)`
  ${responsive({
    xl: {
      maxWidth: '720px',
      width: '720px',
    },
  })};
`;

const Coach = styled(Grade)`
  display: flex;
  flex-direction: column;
  text-align: center;
  font-size: 2.3rem;
  min-width: 250px;
  align-items: center;

  img {
    margin-top: 8px;
    width: 160px;
    height: 160px;
    border-radius: 50%;
  }
`;

const CoachCard = styled(GradeCard)`
  ${responsive({
    md: {
      height: '240px',
    },
  })};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 30px;
  flex-direction: column;
  ${responsive({
    md: {
      flexDirection: 'row',
    },
  })};
`;

const StyledButton = styled(Button)`
  font-size: 3rem;
  font-weight: 700;
  width: 300px;
  height: 80px;
  border-radius: 40px;
  border: 2px solid white;
  display: flex;
  justify-content: center;
  color: black;
  background-color: #ffc464;
  box-shadow: 5px 5px 5px darken(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.2), 2%),
    -5px -5px 5px lighten(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.2), 2%);
  transition: ease all 0.3s;
  cursor: pointer;

  &:hover {
    box-shadow: 10px 10px 10px darken(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.1), 2%),
      -10px -10px 10px lighten(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.1), 2%);
    transform: translateY(-3px);
  }
  &:active {
    transform: scale(0.9);
    box-shadow: inset -1px -1px 3px
        darken(rgba(red(#81c784), green(#81c784), blue(#81c784), 0.3), 2%),
      -10px -10px 10px lighten(rgba(red(#81c784), green(#81c784), blue(#81c784), 0), 2%);
  }
`;

function Home() {
  const dispatch = useDispatch();
  const user = useAppSelector(selectUser);
  const { coach, grade, messages, status } = user;

  const handleClickLogin = (isContinue: boolean) => {
    dispatch(login(grade, coach, isContinue));
  };

  return (
    <Background key="Home" data-testid="Home">
      <StyledContainer fullScreen>
        <ChooserContainer>
          <GradeContainer>
            <StepSelect>
              <Step>Step 1</Step>
              <br />
              Choose your session topic
            </StepSelect>
            {grades.map(({ description }, index) => (
              <GradeCard
                key={index}
                className={cn({ active: index === grade })}
                onClick={() => dispatch(setGrade(index))}
              >
                <Grade>{1 + index}</Grade>
                <span dangerouslySetInnerHTML={{ __html: description }} />
              </GradeCard>
            ))}
          </GradeContainer>

          <CoachContainer>
            <StepSelect>
              <Step>Step 2</Step>
              <br />
              Choose your coach
            </StepSelect>
            {coaches.map(({ description, name }, index) => (
              <CoachCard
                key={index}
                className={cn({ active: index === coach })}
                onClick={() => dispatch(setCoach(index))}
              >
                <Coach>
                  {name}
                  <Image alt={name} src={`${process.env.PUBLIC_URL}/media/images/${name}.png`} />
                </Coach>
                <span dangerouslySetInnerHTML={{ __html: description }} />
              </CoachCard>
            ))}
          </CoachContainer>
        </ChooserContainer>
        <ButtonContainer>
          <StyledButton
            busy={status === STATUS.RUNNING}
            data-testid="Start"
            onClick={() => handleClickLogin(false)}
            size="xl"
            textTransform="uppercase"
            variant="primary"
          >
            <Text ml={2}>Start</Text>
          </StyledButton>
          <StyledButton
            busy={status === STATUS.RUNNING}
            data-testid="Continue"
            disabled={messages[grade][coach].length === 0}
            onClick={() => handleClickLogin(true)}
            size="xl"
            textTransform="uppercase"
            variant="success"
          >
            <Text ml={2}>Continue</Text>
          </StyledButton>
        </ButtonContainer>
      </StyledContainer>
    </Background>
  );
}

export default Home;
